import React from 'react';

import { Grid, TextField } from '@mui/material';
import { useFormContext } from 'react-hook-form';

import { Stack } from '@liscio/ui';

import { CreateAccountSectionProps } from './sectionTypes';
import { LabeledField, SubsectionTitle } from '../CreateAccountFormFieldPrimitives';
import { CreateAccountFormValues } from '../createAccountValidationSchema';
import { InlineRichOptionSelect } from '../InlineRichOptionSelect';
import { stateOptionsAbbreviated } from 'components/Select/option-sets';
import { useHomeData } from 'fetch-utils/users/user-hooks';



export const CreateAccountSpouseDetailsFields: React.FC<
  CreateAccountSectionProps
> = ({ layout = 'responsive' }) => {
  const { data: homeData } = useHomeData();
  const {
    register,
    control,
    formState: { errors },
  } = useFormContext<CreateAccountFormValues>();

  const firmType = (homeData?.data as { firm_type?: string } | undefined)
    ?.firm_type;
  const notCpa = firmType && firmType !== 'cpa';

  const nameBreakpoint = layout === 'single-column' ? 12 : 4;
  const licenseBreakpoint = layout === 'single-column' ? 12 : 3;
  const cityBreakpoint = layout === 'single-column' ? 12 : 6;
  const stateZipBreakpoint = layout === 'single-column' ? 12 : 3;

  return (
    <Stack spacing={2}>
      <SubsectionTitle>
        {notCpa ? "Matter's Spouse Details" : 'Spouse Details'}
      </SubsectionTitle>

      <Grid container spacing={2.5} width="100%" sx={{ m: 0 }}>
        <Grid item xs={12} sm={nameBreakpoint}>
          <LabeledField label="First Name">
            <TextField
              {...register('in_sps_first_name')}
              fullWidth
              error={Boolean(errors.in_sps_first_name)}
              helperText={errors.in_sps_first_name?.message}
              autoComplete="off"
            />
          </LabeledField>
        </Grid>

        <Grid item xs={12} sm={nameBreakpoint}>
          <LabeledField label="Middle Name">
            <TextField
              {...register('in_sps_middle_name')}
              fullWidth
              error={Boolean(errors.in_sps_middle_name)}
              helperText={errors.in_sps_middle_name?.message}
              autoComplete="off"
            />
          </LabeledField>
        </Grid>

        <Grid item xs={12} sm={nameBreakpoint}>
          <LabeledField label="Last Name">
            <TextField
              {...register('in_sps_last_name')}
              fullWidth
              error={Boolean(errors.in_sps_last_name)}
              helperText={errors.in_sps_last_name?.message}
              autoComplete="off"
            />
          </LabeledField>
        </Grid>

        <Grid item xs={12} sm={nameBreakpoint}>
          <LabeledField label="Taxpayer ID">
            <TextField
              {...register('encryptedInSpsSsn')}
              fullWidth
              type="password"
              error={Boolean(errors.encryptedInSpsSsn)}
              helperText={errors.encryptedInSpsSsn?.message}
              autoComplete="off"
            />
          </LabeledField>
        </Grid>

        <Grid item xs={12} sm={nameBreakpoint}>
          <LabeledField label="Birthdate">
            <TextField
              {...register('in_sps_birthdate')}
              fullWidth
              type="date"
              InputLabelProps={{ shrink: true }}
              error={Boolean(errors.in_sps_birthdate)}
              helperText={errors.in_sps_birthdate?.message}
            />
          </LabeledField>
        </Grid>

        <Grid item xs={12} sm={licenseBreakpoint}>
          <LabeledField label="Drivers License #">
            <TextField
              {...register('in_sps_driver_license')}
              fullWidth
              error={Boolean(errors.in_sps_driver_license)}
              helperText={errors.in_sps_driver_license?.message}
              autoComplete="off"
            />
          </LabeledField>
        </Grid>

        <Grid item xs={12} sm={licenseBreakpoint}>
          <LabeledField label="Date Issued">
            <TextField
              {...register('in_sps_license_issue_date')}
              fullWidth
              type="date"
              InputLabelProps={{ shrink: true }}
              error={Boolean(errors.in_sps_license_issue_date)}
              helperText={errors.in_sps_license_issue_date?.message}
            />
          </LabeledField>
        </Grid>

        <Grid item xs={12} sm={licenseBreakpoint}>
          <LabeledField label="Date Expired">
            <TextField
              {...register('in_sps_license_expiry_date')}
              fullWidth
              type="date"
              InputLabelProps={{ shrink: true }}
              error={Boolean(errors.in_sps_license_expiry_date)}
              helperText={errors.in_sps_license_expiry_date?.message}
            />
          </LabeledField>
        </Grid>

        <Grid item xs={12} sm={licenseBreakpoint}>
          <LabeledField label="State of issue">
            <InlineRichOptionSelect
              name="in_sps_license_state"
              control={control}
              data={stateOptionsAbbreviated}
              error={errors.in_sps_license_state}
              placeholder="Select..."
            />
          </LabeledField>
        </Grid>
      </Grid>

      <SubsectionTitle sx={{ mt: 1 }}>Spouse address</SubsectionTitle>
      <Grid container spacing={2.5} width="100%" sx={{ m: 0 }}>
        <Grid item xs={12}>
          <LabeledField label="Line 1">
            <TextField
              {...register('in_sps_address_line_1')}
              fullWidth
              error={Boolean(errors.in_sps_address_line_1)}
              helperText={errors.in_sps_address_line_1?.message}
              autoComplete="address-line1"
            />
          </LabeledField>
        </Grid>

        <Grid item xs={12}>
          <LabeledField label="Line 2">
            <TextField
              {...register('in_sps_address_line_2')}
              fullWidth
              error={Boolean(errors.in_sps_address_line_2)}
              helperText={errors.in_sps_address_line_2?.message}
              autoComplete="address-line2"
            />
          </LabeledField>
        </Grid>

        <Grid item xs={12} sm={cityBreakpoint}>
          <LabeledField label="City">
            <TextField
              {...register('in_sps_address_city')}
              fullWidth
              error={Boolean(errors.in_sps_address_city)}
              helperText={errors.in_sps_address_city?.message}
              autoComplete="address-level2"
            />
          </LabeledField>
        </Grid>

        <Grid item xs={12} sm={stateZipBreakpoint}>
          <LabeledField label="State">
            <TextField
              {...register('in_sps_address_state')}
              fullWidth
              error={Boolean(errors.in_sps_address_state)}
              helperText={errors.in_sps_address_state?.message}
              autoComplete="address-level1"
            />
          </LabeledField>
        </Grid>

        <Grid item xs={12} sm={stateZipBreakpoint}>
          <LabeledField label="Zip">
            <TextField
              {...register('in_sps_address_zip')}
              fullWidth
              error={Boolean(errors.in_sps_address_zip)}
              helperText={errors.in_sps_address_zip?.message}
              autoComplete="postal-code"
            />
          </LabeledField>
        </Grid>

        <Grid item xs={12}>
          <LabeledField label="Country">
            <TextField
              {...register('in_sps_address_country')}
              fullWidth
              error={Boolean(errors.in_sps_address_country)}
              helperText={errors.in_sps_address_country?.message}
              autoComplete="country-name"
            />
          </LabeledField>
        </Grid>
      </Grid>
    </Stack>
  );
};

export default CreateAccountSpouseDetailsFields;
